import React, { useState, useEffect } from "react";
import '../Login/Login.scss';
import axios from 'axios';
import { useNavigate, Link } from "react-router-dom";
import { GuestForm } from "../GuestForm";
import { useNoProfile } from "../../hooks/useProfileData";
import { useToast } from "../../Contexts/ToastContext";
import { GoogleSignInButton } from "../GoogleSignInButton";
import { setPendingCouponCode } from "../../utils/couponsApi";
import { setPreferredNotifyChannel } from "../../utils/notifyChannel";
import {
  buildRegisterPayload,
  getRegistrationValidity,
  navigateAfterRegistration,
  validateRegistrationForm,
} from "./registerHelpers";
import { noAutofillProps } from "../noAutofillProps";

export function Register() {
  useNoProfile();
  const { showToast } = useToast();

  useEffect(() => {
    localStorage.removeItem('resendTime');
  }, []);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    pincode: ''
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [showGuestForm, setShowGuestForm] = useState(false);
  const navigate = useNavigate();

  const { hasValidPhone, hasValidEmail } = getRegistrationValidity(formData);
  const showVerifyOptions = hasValidPhone || hasValidEmail;

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const registerWithVerification = async (channel) => {
    const validationError = validateRegistrationForm(formData);
    if (validationError) {
      setResponseMessage(validationError);
      return;
    }

    if (channel === 'email' && !hasValidEmail) {
      setResponseMessage('Please enter a valid email address.');
      return;
    }

    if ((channel === 'sms' || channel === 'whatsapp') && !hasValidPhone) {
      setResponseMessage('Please enter a valid 10-digit mobile number.');
      return;
    }

    setLoading(true);
    setResponseMessage('');

    try {
      const response = await axios.post(
        '/users/register',
        buildRegisterPayload(formData, channel)
      );

      if (response.status === 200) {
        setPendingCouponCode('WELCOME20');
        if (channel === 'whatsapp' || channel === 'sms' || channel === 'email') {
          setPreferredNotifyChannel(channel);
        }
        navigateAfterRegistration(navigate, {
          channel,
          formData,
          responseData: response.data?.data || {},
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      const status = error.response?.status;
      const msg = error.response?.data?.msg || error.response?.data?.message;
      if (status === 409) {
        setResponseMessage(
          `${msg || 'This account already exists.'} Try logging in instead.`
        );
      } else {
        setResponseMessage(msg || 'Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setResponseMessage('Please choose Phone OTP, WhatsApp OTP, or Email code to verify and create your account.');
  };

  return (
    <div className="web-authPage">
      <div className="auth-container">
        <div
          className="LazyLoad is-visible"
          style={{ width: '100%', background: 'rgb(255, 242, 223)' }}
        >
          <picture className="img-responsive" style={{ width: '100%' }}>
            <source
              srcSet="//assets.myntassets.com/f_webp,dpr_1.5,q_60,w_400,c_limit,fl_progressive/assets/images/2023/10/29/9610da61-a1a4-4215-b1fa-f612242d10e61698602536819-Flat_200--1-.jpg"
              type="image/webp"
            />
            <img
              src="//assets.myntassets.com/dpr_1.5,q_60,w_400,c_limit,fl_progressive/assets/images/2023/10/29/9610da61-a1a4-4215-b1fa-f612242d10e61698602536819-Flat_200--1-.jpg"
              className="img-responsive preLoad loaded"
              alt="Register Background"
              title=""
              style={{ width: '100%' }}
            />
          </picture>
        </div>
        <div className="signInContainer">
          <div className="welcome-header">
            Create Account
            <span className="welcome-header-small">Join us today!</span>
          </div>
          <div className="mobileInputContainer">
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="form-group">
                <input
                  type="text"
                  name="firstName"
                  className="form-control mobileNumberInput"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber">
                  First Name <span style={{ color: '#ff5722' }}>*</span>
                </span>
                <i className="bar"></i>
              </div>

              <div className="form-group">
                <input
                  type="text"
                  name="lastName"
                  className="form-control mobileNumberInput"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber">
                  Last Name <span style={{ color: '#ff5722' }}>*</span>
                </span>
                <i className="bar"></i>
              </div>

              <div className="form-group">
                <input
                  type="tel"
                  name="phone"
                  className="form-control mobileNumberInput"
                  placeholder="Mobile Number"
                  maxLength="10"
                  value={formData.phone}
                  onChange={handleInputChange}
                  inputMode="numeric"
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber">
                  +91
                  <span style={{ padding: '0px 10px', position: 'relative', bottom: '1px' }}>|</span>
                  Mobile Number <span className="field-optional-label">(or email below)</span>
                </span>
                <i className="bar"></i>
              </div>

              <div className="auth-field-or">OR</div>

              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  className="form-control mobileNumberInput"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber">
                  Email Address <span className="field-optional-label">(or mobile above)</span>
                </span>
                <i className="bar"></i>
              </div>

              {showVerifyOptions && (
                <div className="login-otp-options" style={{ marginBottom: '12px' }}>
                  <span className="login-otp-options__label">Verify using</span>
                  <div className="login-otp-options__links">
                    <button
                      type="button"
                      className={`login-otp-options__btn ${hasValidPhone ? '' : 'login-otp-options__btn--disabled'}`}
                      disabled={!hasValidPhone || loading}
                      onClick={() => registerWithVerification('sms')}
                    >
                      <i className="bi bi-phone"></i>
                      Phone OTP
                    </button>
                    <span className="login-otp-options__divider">|</span>
                    <button
                      type="button"
                      className={`login-otp-options__btn ${hasValidPhone ? '' : 'login-otp-options__btn--disabled'}`}
                      disabled={!hasValidPhone || loading}
                      onClick={() => registerWithVerification('whatsapp')}
                    >
                      <i className="bi bi-whatsapp"></i>
                      WhatsApp OTP
                    </button>
                    <span className="login-otp-options__divider">|</span>
                    <button
                      type="button"
                      className={`login-otp-options__btn ${hasValidEmail ? '' : 'login-otp-options__btn--disabled'}`}
                      disabled={!hasValidEmail || loading}
                      onClick={() => registerWithVerification('email')}
                    >
                      <i className="bi bi-envelope"></i>
                      Email code
                    </button>
                  </div>
                </div>
              )}

              <div className="form-group">
                <input
                  type="tel"
                  name="pincode"
                  className="form-control mobileNumberInput"
                  placeholder="Pincode"
                  maxLength="6"
                  value={formData.pincode}
                  onChange={handleInputChange}
                  inputMode="numeric"
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber">
                  Pincode <span className="field-optional-label">(optional)</span>
                </span>
                <i className="bar"></i>
              </div>

              {responseMessage && (
                <div className="responseMessage">
                  <i className="bi bi-exclamation-circle-fill"></i>
                  <span>{responseMessage}</span>
                </div>
              )}

              <div className="midLinks">
                By continuing, I agree to the{' '}
                <a href="/terms">Terms of Use</a> &amp;{' '}
                <a href="/privacy">Privacy Policy</a>
              </div>

              <button type="submit" className="submitBottomOption" disabled={loading || !showVerifyOptions}>
                {loading ? 'Creating Account...' : 'CREATE ACCOUNT'}
              </button>
              {!showVerifyOptions && (
                <p className="auth-hint" style={{ marginTop: '8px', fontSize: '13px', color: '#6b7280', textAlign: 'center' }}>
                  Enter mobile or email above, then choose a verification method.
                </p>
              )}

              <div className="auth-divider">
                <span className="auth-divider__text">OR</span>
              </div>

              <div className="auth-social-buttons">
                <GoogleSignInButton
                  variant="web"
                  disabled={loading}
                  onError={(msg) => setResponseMessage(msg)}
                />
                <button type="button" className="auth-social-btn auth-social-btn-facebook" onClick={() => showToast({ message: 'Facebook sign-up coming soon' })}>
                  <svg className="auth-social-icon" viewBox="0 0 24 24" width="20" height="20"><path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Continue with Facebook
                </button>
              </div>

              <div className="auth-divider">
                <span className="auth-divider__text">OR</span>
              </div>

              <button type="button" className="guest-btn" onClick={() => setShowGuestForm(true)}>
                LOGIN AS GUEST
              </button>
            </form>
            {showGuestForm && (
              <GuestForm onClose={() => setShowGuestForm(false)} onSuccess={() => setShowGuestForm(false)} />
            )}
          </div>

          <div className="get-help">
            Already have an account? <Link to="/auth/login">Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
